import React, { useState, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Mail, Send, Copy, Check, Edit2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { PilotCrmService } from '@/services/pilot-crm.service';
import { EmailTrackingService } from '@/services/email-tracking.service';
import { EmployerLead } from '@/models/pilot-crm.model';
import { PILOT_CRM_EMAIL_TEMPLATES } from '@/data/pilot-crm-email-templates';
import { Button, Card, FormSelect, FormInput, FormTextarea } from '@/components/common';

export interface EmailComposeModalProps {
  lead: EmployerLead;
  onClose: () => void;
  onSent?: () => void;
}

const N8N_WEBHOOK_BASE_URL = import.meta.env.VITE_N8N_WEBHOOK_URL || '';

const mergeTemplate = (text: string, lead: EmployerLead): string => {
  const firstName = lead.contactName?.trim().split(' ')[0] || 'there';
  return text
    .replace(/{{CompanyName}}/g, lead.companyName || 'your company')
    .replace(/{{FirstName}}/g, firstName)
    .replace(/{{ContactName}}/g, lead.contactName || 'there')
    .replace(/{{ContactEmail}}/g, lead.contactEmail || '')
    .replace(/{{Industry}}/g, lead.industry || 'your industry');
};

export const EmailComposeModal: React.FC<EmailComposeModalProps> = ({
  lead,
  onClose,
  onSent,
}) => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(
    PILOT_CRM_EMAIL_TEMPLATES[0]?.id || ''
  );
  const [isEditing, setIsEditing] = useState(false);
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');
  const [copied, setCopied] = useState(false);
  const [sendMethod, setSendMethod] = useState<'n8n' | 'manual'>('manual');

  const selectedTemplate = useMemo(() => {
    return PILOT_CRM_EMAIL_TEMPLATES.find((t) => t.id === selectedTemplateId);
  }, [selectedTemplateId]);

  const mergedSubject = useMemo(() => {
    if (isEditing) return editedSubject;
    return selectedTemplate ? mergeTemplate(selectedTemplate.subject, lead) : '';
  }, [selectedTemplate, lead, isEditing, editedSubject]);

  const mergedBody = useMemo(() => {
    if (isEditing) return editedBody;
    return selectedTemplate ? mergeTemplate(selectedTemplate.body, lead) : '';
  }, [selectedTemplate, lead, isEditing, editedBody]);

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setIsEditing(false);
    const template = PILOT_CRM_EMAIL_TEMPLATES.find((t) => t.id === templateId);
    if (template) {
      setEditedSubject(mergeTemplate(template.subject, lead));
      setEditedBody(mergeTemplate(template.body, lead));
    }
  };

  const handleEditToggle = () => {
    if (!isEditing) {
      setEditedSubject(mergedSubject);
      setEditedBody(mergedBody);
    }
    setIsEditing(!isEditing);
  };

  const copyToClipboard = async () => {
    const text = `To: ${lead.contactEmail}\nSubject: ${mergedSubject}\n\n${mergedBody}`;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sendViaN8nMutation = useMutation({
    mutationFn: async () => {
      if (!profile?.id) throw new Error('Not authenticated');

      const messageId = EmailTrackingService.generateMessageId();

      if (N8N_WEBHOOK_BASE_URL) {
        const response = await fetch(`${N8N_WEBHOOK_BASE_URL}/pilot-crm-email-outreach`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            leadId: lead.id,
            templateId: selectedTemplateId,
            subject: mergedSubject,
            body: mergedBody,
            toEmail: lead.contactEmail,
            messageId,
            userId: profile.id,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to trigger email workflow');
        }
      }

      await EmailTrackingService.logEmailSent(
        lead.id,
        messageId,
        mergedSubject,
        mergedBody,
        lead.contactEmail,
        selectedTemplateId,
        profile.id
      );

      await PilotCrmService.createInteraction(
        {
          leadId: lead.id,
          type: 'email_sent',
          channel: 'email',
          subject: mergedSubject,
          summary: `Email sent: ${mergedSubject}`,
          raw: mergedBody,
        },
        profile.id
      );

      if (lead.status === 'new') {
        await PilotCrmService.updateLead(lead.id, { status: 'invited' }, profile.id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pilot-crm-interactions', lead.id] });
      queryClient.invalidateQueries({ queryKey: ['pilot-crm-lead', lead.id] });
      queryClient.invalidateQueries({ queryKey: ['pilot-crm-leads'] });
      onSent?.();
      onClose();
    },
  });

  const markAsSentMutation = useMutation({
    mutationFn: async () => {
      if (!profile?.id) throw new Error('Not authenticated');

      const messageId = EmailTrackingService.generateMessageId();

      await EmailTrackingService.logEmailSent(
        lead.id,
        messageId,
        mergedSubject,
        mergedBody,
        lead.contactEmail,
        selectedTemplateId,
        profile.id
      );

      await PilotCrmService.createInteraction(
        {
          leadId: lead.id,
          type: 'email_sent',
          channel: 'email',
          subject: mergedSubject,
          summary: `Email sent: ${mergedSubject}`,
          raw: mergedBody,
        },
        profile.id
      );

      if (lead.status === 'new') {
        await PilotCrmService.updateLead(lead.id, { status: 'invited' }, profile.id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pilot-crm-interactions', lead.id] });
      queryClient.invalidateQueries({ queryKey: ['pilot-crm-lead', lead.id] });
      queryClient.invalidateQueries({ queryKey: ['pilot-crm-leads'] });
      onSent?.();
      onClose();
    },
  });

  const isPending = sendViaN8nMutation.isPending || markAsSentMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        <Card className="relative max-w-2xl w-full z-10">
          <div className="flex items-center gap-2 mb-6">
            <Mail className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">Compose Email</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <FormSelect
                  label="Template"
                  options={PILOT_CRM_EMAIL_TEMPLATES.map((template) => ({
                    value: template.id,
                    label: template.name,
                  }))}
                  value={selectedTemplateId}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                />
              </div>
              <div className="pt-6">
                <Button
                  variant="outline"
                  size="sm"
                  icon={Edit2}
                  onClick={handleEditToggle}
                >
                  {isEditing ? 'Reset' : 'Edit'}
                </Button>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="text-sm text-gray-500 mb-2">
                <span className="font-medium">To:</span> {lead.contactEmail}
              </div>

              {isEditing ? (
                <>
                  <FormInput
                    label="Subject"
                    value={editedSubject}
                    onChange={(e) => setEditedSubject(e.target.value)}
                  />
                  <FormTextarea
                    label="Body"
                    rows={8}
                    value={editedBody}
                    onChange={(e) => setEditedBody(e.target.value)}
                    className="mt-4"
                  />
                </>
              ) : (
                <>
                  <div className="mb-4">
                    <span className="text-xs font-semibold text-gray-500 uppercase">
                      Subject
                    </span>
                    <p className="text-sm font-medium text-gray-900 mt-1">{mergedSubject}</p>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-gray-500 uppercase">
                      Body
                    </span>
                    <div className="mt-1 bg-white border border-gray-200 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap max-h-64 overflow-y-auto">
                      {mergedBody}
                    </div>
                  </div>
                </>
              )}
            </div>

            {N8N_WEBHOOK_BASE_URL && (
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="sendMethod"
                    value="manual"
                    checked={sendMethod === 'manual'}
                    onChange={() => setSendMethod('manual')}
                    className="text-blue-600"
                  />
                  <span className="text-sm text-gray-700">Copy & send manually</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="sendMethod"
                    value="n8n"
                    checked={sendMethod === 'n8n'}
                    onChange={() => setSendMethod('n8n')}
                    className="text-blue-600"
                  />
                  <span className="text-sm text-gray-700">Send via automation</span>
                </label>
              </div>
            )}
          </div>

          <div className="flex flex-wrap justify-end gap-3 pt-6 border-t border-gray-200 mt-6">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="outline"
              icon={copied ? Check : Copy}
              onClick={copyToClipboard}
            >
              {copied ? 'Copied!' : 'Copy to Clipboard'}
            </Button>
            {sendMethod === 'n8n' && N8N_WEBHOOK_BASE_URL ? (
              <Button
                variant="primary"
                icon={Send}
                onClick={() => sendViaN8nMutation.mutate()}
                isLoading={isPending}
              >
                Send Email
              </Button>
            ) : (
              <Button
                variant="primary"
                icon={Check}
                onClick={() => markAsSentMutation.mutate()}
                isLoading={isPending}
              >
                Mark as Sent
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};
